/* eslint-disable no-console */
// This is one of the few places throughout the codebase that really cannot use the loggingService.
// Utilizing the loggingService would require auth to be completed so it is safest to just use console logs
// instead of having the potential for sending a request while unauthorized triggering a potential mess.

import Axios from 'axios';
import { encode as b64enc } from 'base64-arraybuffer';
import { z } from 'zod';
import randomstring from 'randomstring';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  EMPTY,
  firstValueFrom,
  from,
  fromEvent,
  merge,
  Observable,
  timer,
  using,
  zip,
} from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  repeat,
  switchMap,
  take,
} from 'rxjs/operators';

const USER_DETAILS_ENDPOINT = '/asst/api/user/details';

export const authenticationConfig = z.object({
  domain: z.string().nonempty(),
  clientId: z.string().nonempty(),
});

interface AuthenticationConfig extends z.infer<typeof authenticationConfig> { }

export const platformConfig = z.object({
  auth: authenticationConfig,
  config: z.looseObject({
    isClientFacing: z.boolean().optional().default(true),
  }),
});

const openIdConfiguration = z.object({
  authorization_endpoint: z.string(),
  jwks_uri: z.string(),
  token_endpoint: z.string(),
  userinfo_endpoint: z.string(),
  end_session_endpoint: z.string(),
});

interface OpenIdConfiguration extends z.infer<typeof openIdConfiguration> { }

export const TokenResponse = z.object({
  id_token: z.string().optional(),
  access_token: z.string(),
  refresh_token: z.string(),
  token_type: z.literal('Bearer'),
  // standard says recommended, we'll require it
  expires_in: z.number(),
});

interface TokenResponse extends z.infer<typeof TokenResponse> {}

/* Constants used for local storage */
const STORAGE_ID_TOKEN = 'id_token';
const STORAGE_ACCESS_TOKEN = 'access_token';
const STORAGE_REFRESH_TOKEN = 'refresh_token';
const STORAGE_EXPIRATION_TIME = 'expiration_time';
const STORAGE_STATE = 'oauth2_state';
const STORAGE_VERIFIER = 'pkce_verifier';
const STORAGE_REFRESHING = 'oauth2_refreshing';
const STORAGE_EXPLICIT_LOGOUT = 'oauth2_explicit_logout';
const REFRESH_MAX_TIME = 5000;

function attemptDateParse(date: string | null) {
  if (!date) {
    return null;
  }
  const millis = Date.parse(date);
  if (isNaN(millis)) {
    console.warn(`invalid date string ${date} received.`);
    return null;
  }
  return new Date(millis);
}

/* Basic operation:
 * We have a series of subjects (because we need to modify) that are synchronized
 * with the state of localStorage (to ensure cross document token sharing).
 *
 * These subjects can be used to hook into things like request interceptors
 * to ensure that requests are held until we have a valid token.
 * Additionally, various login workflow things manipulate these subjects
 * and several un-ending streams are spawned to handle things like the auto-refresh
 * workflow.
 */
export const accessToken$: BehaviorSubject<string | null> = new BehaviorSubject(
  localStorage.getItem(STORAGE_ACCESS_TOKEN),
);

export const refreshToken$: BehaviorSubject<string | null> = new BehaviorSubject(
  localStorage.getItem(STORAGE_REFRESH_TOKEN),
);

const idToken$: BehaviorSubject<string | null> = new BehaviorSubject(localStorage.getItem(STORAGE_ID_TOKEN));

export const expirationTime$: BehaviorSubject<Date | null> = new BehaviorSubject(
  attemptDateParse(localStorage.getItem(STORAGE_EXPIRATION_TIME)),
);

function getRefreshStartTime() {
  const refreshStartTime = localStorage.getItem(STORAGE_REFRESHING);
  const startTime = attemptDateParse(refreshStartTime)?.getTime() ?? 0;
  return startTime;
}

function isRefreshActive() {
  const startTime = getRefreshStartTime();
  const curTime = Date.now() - REFRESH_MAX_TIME;
  // start -10 seconds ago
  // now - 5 == -5
  return startTime >= curTime;
}
export const currentlyRefreshing$: BehaviorSubject<boolean> = new BehaviorSubject(isRefreshActive());

export const explicitLogout$: BehaviorSubject<boolean> = new BehaviorSubject(
  localStorage.getItem(STORAGE_EXPLICIT_LOGOUT) != null,
);

export const loginError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

// Publicly visible access token
export const existingAccessToken$: Observable<string> = accessToken$
  .pipe(filter((t) => t !== null))
  .pipe(map((t) => t || ''));

export function existingAccessToken(): Promise<string> {
  return firstValueFrom(accessToken$.pipe(filter((t) => t !== null)).pipe(map((t) => t || '')));
}

export function getLatestAccessToken(): string | null {
  return accessToken$.getValue();
}

/* We want to make sure that we always have the latest data authentication state.
 * We hook into storage events and pipe these into the accessToken/etc
 * behavior subject to make sure we are synchronized with other tabs
 */
const storageEvent$ = fromEvent<StorageEvent>(window, 'storage');
storageEvent$
  .pipe(filter((ev) => ev.key === STORAGE_ACCESS_TOKEN))
  .pipe(map((ev) => ev.newValue))
  .subscribe(accessToken$);

storageEvent$
  .pipe(filter((ev) => ev.key === STORAGE_ID_TOKEN))
  .pipe(map((ev) => ev.newValue))
  .subscribe(idToken$);

storageEvent$
  .pipe(filter((ev) => ev.key === STORAGE_REFRESH_TOKEN))
  .pipe(map((ev) => ev.newValue))
  .subscribe(refreshToken$);

storageEvent$
  .pipe(filter((ev) => ev.key === STORAGE_EXPIRATION_TIME))
  .pipe(map((ev) => attemptDateParse(ev.newValue)))
  .subscribe(expirationTime$);

storageEvent$
  .pipe(filter((ev) => ev.key === STORAGE_EXPLICIT_LOGOUT))
  .pipe(map((ev) => ev.newValue != null))
  .subscribe(explicitLogout$);

storageEvent$
  .pipe(filter((ev) => ev.key === STORAGE_REFRESHING))
  .pipe(map(isRefreshActive))
  .subscribe(currentlyRefreshing$);

function createRefreshCheckInterval() {
  const curTime = Date.now() - REFRESH_MAX_TIME;
  const getRefreshTime = getRefreshStartTime() - curTime;
  if (curTime > 0) {
    timer(getRefreshTime).subscribe(() => {
      const isStillRefresh = isRefreshActive();
      if (!isStillRefresh) {
        setCurrentlyRefreshing(false);
      }
    });
  } else {
    setCurrentlyRefreshing(false);
  }
}
const windowLoad$ = fromEvent<Event>(window, 'load');

merge(currentlyRefreshing$, windowLoad$)
  .pipe(
    map(isRefreshActive),
    filter(v => v)
  )
  .subscribe(createRefreshCheckInterval);

// we also have to account for 'clear'
storageEvent$.pipe(filter((ev) => ev.key === null)).subscribe((_ev) => {
  accessToken$.next(null);
  idToken$.next(null);
  refreshToken$.next(null);
  expirationTime$.next(null);
  explicitLogout$.next(false);
  currentlyRefreshing$.next(false);
});

const appJsonOverride =
  (import.meta.env &&
    import.meta.env.VITE_APP_USE_LOCAL_JSON &&
    import.meta.env.VITE_APP_OVERRIDE_JSON &&
    typeof import.meta.env.VITE_APP_OVERRIDE_JSON === 'string' &&
    `${import.meta.env.VITE_APP_OVERRIDE_JSON}.json`) ||
  '/asst/api/config/frontend';
const appJson = from(Axios.get(appJsonOverride)).pipe(map((response) => platformConfig.parse(response.data)));
export const isClientFacingFromConfig$: Observable<boolean> = appJson.pipe(
  map((platform) => platform.config?.isClientFacing ?? true),
  catchError(() => from([true])),
);

const authConfig = appJson.pipe(map((platform) => platform.auth));

async function generateChallenge(verifier: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const b64 = b64enc(digest);
  // The internet says this is important...
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function setState(state: string) {
  sessionStorage.setItem(STORAGE_STATE, state);
}

function setVerifier(verifier: string) {
  sessionStorage.setItem(STORAGE_VERIFIER, verifier);
}

function getState() {
  return sessionStorage.getItem(STORAGE_STATE);
}

function getVerifier() {
  return sessionStorage.getItem(STORAGE_VERIFIER);
}

export function setIdToken(token: string | null) {
  if (token) {
    localStorage.setItem(STORAGE_ID_TOKEN, token);
  } else {
    localStorage.removeItem(STORAGE_ID_TOKEN);
  }
  idToken$.next(token);
}

export function setAccessToken(token: string | null) {
  if (token) {
    localStorage.setItem(STORAGE_ACCESS_TOKEN, token);
  } else {
    localStorage.removeItem(STORAGE_ACCESS_TOKEN);
  }
  accessToken$.next(token);
}

export function setRefreshToken(token: string | null | undefined) {
  if (token) {
    localStorage.setItem(STORAGE_REFRESH_TOKEN, token);
  } else {
    localStorage.removeItem(STORAGE_REFRESH_TOKEN);
  }
  refreshToken$.next(token || null);
}

export function setExpirationTime(time: Date | null) {
  if (time) {
    localStorage.setItem(STORAGE_EXPIRATION_TIME, time.toISOString());
  } else {
    localStorage.removeItem(STORAGE_EXPIRATION_TIME);
  }
  expirationTime$.next(time);
}

export function setCurrentlyRefreshing(flag: boolean) {
  if (flag) {
    const time = new Date().toISOString();
    localStorage.setItem(STORAGE_REFRESHING, time);
    currentlyRefreshing$.next(true);
  } else {
    localStorage.removeItem(STORAGE_REFRESHING);
    currentlyRefreshing$.next(false);
  }
}

function clearLoginIntermediates() {
  sessionStorage.removeItem(STORAGE_STATE);
  sessionStorage.removeItem(STORAGE_VERIFIER);
}

function generateRedirectUri(): string {
  return `${window.location.protocol}//${window.location.host}/`;
}

function generateLoginUri(
  authConfig: AuthenticationConfig,
  oidcConfig: OpenIdConfiguration,
  challenge: string,
  state: string,
) {
  const params = new URLSearchParams();
  params.set('client_id', authConfig.clientId);
  params.set('response_type', 'code');
  params.set('state', state);
  params.set('code_challenge', challenge);
  params.set('code_challenge_method', 'S256');
  params.set('redirect_uri', generateRedirectUri());
  params.set('scope', 'openid profile offline_access');
  const uri = `${oidcConfig.authorization_endpoint}?${params.toString()}`;
  return uri;
}

function getOidcConfig(ac: AuthenticationConfig) {
  return defer(() => Axios.get(`${ac.domain}/.well-known/openid-configuration`)).pipe(
    map((response) => openIdConfiguration.parse(response.data)),
  );
}

export const authParamsAndOidc = authConfig.pipe(
  concatMap((params) => getOidcConfig(params).pipe(map((oidc) => ({ auth: params, oidc })))),
);

function beginLogin() {
  // First we contstruct an rxjs pipeline that will setup the requisite state we need
  const state = randomstring.generate(12);
  // PKCE challenge
  const verifier = randomstring.generate(64);
  const challenge = defer(() => from(generateChallenge(verifier)));

  return zip(authParamsAndOidc, challenge).subscribe(([authAndOidc, challenge]) => {
    setState(state);
    setVerifier(verifier);
    const uri = generateLoginUri(authAndOidc.auth, authAndOidc.oidc, challenge, state);
    console.debug(`login navigating to ${uri}`);
    location.href = uri;
  });
}

function startMissingAccessTokenRefresh() {
  combineLatest([accessToken$, refreshToken$, explicitLogout$])
    // When accessToken is null and there is no explicit logout request
    .pipe(filter(([accessToken, refreshToken, logout]) => accessToken == null && refreshToken == null && !logout))
    // We should only attempt 1 login per page load
    .pipe(take(1))
    .subscribe(() => {
      console.debug('performing a login');
      beginLogin();
    });
}

function clearTokenIfExpired() {
  const time = attemptDateParse(localStorage.getItem(STORAGE_EXPIRATION_TIME));
  if (time && time < new Date()) {
    console.debug('Cleared expired token.');
    setAccessToken(null);
  } else {
    console.debug('No expiration detected, skipping clearToken()');
  }
}

function completeLogin(code: string, state: string) {
  const storedState = getState();
  const verifier = getVerifier();
  // We tried, so erase them
  clearLoginIntermediates();
  if (storedState !== state) {
    console.warn('state mismatch');
    loginError$.next(true);
    return;
  }
  if (verifier === null) {
    console.warn('missing verifier');
    loginError$.next(true);
    return;
  }

  const tokenRequest = authParamsAndOidc.pipe(
    concatMap((params) => {
      const formData = new URLSearchParams();
      formData.set('client_id', params.auth.clientId);
      formData.set('code', code);
      // Verify this in the token response at some point?
      formData.set('state', state);
      formData.set('code_verifier', verifier);
      formData.set('grant_type', 'authorization_code');
      formData.set('redirect_uri', generateRedirectUri());
      return defer(() => Axios.post(params.oidc.token_endpoint, formData))
        .pipe(map((response) => TokenResponse.parse(response.data)))
        .pipe(
          switchMap(async (token) => {
            const body = { id_token: token.id_token };
            if (token.id_token) {
              await Axios.post(USER_DETAILS_ENDPOINT, body).catch((error) =>
                console.warn('An error occurred saving user details:', (error as any).message),
              );
            }
            return token;
          }),
        );
    }),
  );

  tokenRequest.subscribe({
    next: (tokens) => {
      const expiration = new Date(Date.now() + tokens.expires_in * 1000);
      setAccessToken(tokens.access_token);
      setRefreshToken(tokens.refresh_token);
      setExpirationTime(expiration);

      // We probably just navigated and this won't do anything, but in case...
      startMissingAccessTokenRefresh();
      // Send the id token to the backend
      location.href = location.pathname;
    },
    error: () => {
      loginError$.next(true);
    },
  });
}

const INJ_TOKEN = 'injectAccessToken';

export function startup() {
  console.debug('startup triggered');
  // First first things first, if accessToken is expired, remove it
  clearTokenIfExpired();
  // First things first, we need to decide if we were attempting to complete a login
  const search = new URLSearchParams(location.search);
  const code = search.get('code');
  const state = search.get('state');
  // We are trying to finish a login
  if (code && state) {
    console.info('login completion parameters received');
    completeLogin(code, state);
  } else {
    const injectAccessToken = search.get(INJ_TOKEN);
    search.delete(INJ_TOKEN);
    // Both defined and not empty
    if (injectAccessToken) {
      console.debug('received injected access token');
      setAccessToken(injectAccessToken);
      // Now that the access token is injected we should clear it from the url
      window.location.search = search.toString();
    }
    // We need to decide if we are doing a fresh login
    // This should happen when there is no explicit logout and we have no accessToken
    console.debug('no login completion parameters, continuing');
    startMissingAccessTokenRefresh();
  }
}

const loggedOutResult: RefreshResult = { idToken: null, accessToken: null, refreshToken: null, expirationTime: null };
export const loggedOut$: Observable<RefreshResult> = from([loggedOutResult]);

// What to do when we know that the current access token has expired?

function setExplicitLogout() {
  localStorage.setItem(STORAGE_EXPLICIT_LOGOUT, 'logout');
  explicitLogout$.next(true);
}

function clearExplicitLogout() {
  localStorage.removeItem(STORAGE_EXPLICIT_LOGOUT);
  explicitLogout$.next(false);
}

export async function logout() {
  setExplicitLogout();
  setRefreshToken(null);
  setAccessToken(null);
  setExpirationTime(null);
  // Do the rebound thing
  const { auth, oidc } = await firstValueFrom(authParamsAndOidc);
  const params = new URLSearchParams();
  params.set('client_id', auth.clientId);
  params.set('post_logout_redirect_uri', generateRedirectUri());
  location.href = `${oidc.end_session_endpoint}?${params.toString()}`;
}

// This add a queryParam option to *force* reset the auth in case everything else fails
// This is the emergency pull
const hashSplit = window.location.hash.split('?');
if (hashSplit.length > 1 && hashSplit[1].indexOf('resetAuth') >= 0) {
  logout();
}

export function explicitLogin() {
  clearExplicitLogout();
  // And then the automatic login should take it from here
}

export function isUnauthorized() {
  console.debug('Unauthorized access attempted, removing existing acess token...');
  // Clear unauthorized access token to trigger automatic refresh
  setAccessToken(null);
}

export interface RefreshResult {
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  expirationTime: Date | null;
}

export function performAutomaticRefresh(
  accessToken: string | null,
  refreshToken: string | null,
): Observable<RefreshResult> {
  return using(
    () => {
      setCurrentlyRefreshing(true);
      return {
        unsubscribe() {
          setCurrentlyRefreshing(false);
        },
      };
    },
    () =>
      authParamsAndOidc.pipe(
        switchMap(({ auth, oidc }) => {
          const formData = new URLSearchParams();
          formData.set('client_id', auth.clientId);
          if (accessToken) {
            formData.set('access_token', accessToken);
          }
          formData.set('grant_type', 'refresh_token');
          if (refreshToken) {
            formData.set('refresh_token', refreshToken);
          }
          // don't set scope, just get the same one
          return defer(() =>
            Axios.post(oidc.token_endpoint, formData, {
              headers: { 'content-type': 'application/x-www-form-urlencoded' },
            }),
          )
            .pipe(map((response) => TokenResponse.parse(response.data)))
            .pipe(
              map(
                (token) =>
                ({
                  // no need to store the id_token here to the , only on initial authentication
                  idToken: token.id_token,
                  accessToken: token.access_token,
                  refreshToken: token.refresh_token,
                  expirationTime: new Date(Date.now() + token.expires_in * 1000),
                } as RefreshResult)
              )
            )
            .pipe(
              catchError((err) => {
                // This probably indicates that our credentials are bad somehow
                // err contains the axios response here
                if (err.response && err.response.status && err.response.status < 500) {
                  console.warn('token refresh was rejected');
                  // this should trigger begin login elsewhere
                  return loggedOut$;
                }
                console.warn('token refresh ended in error', err);
                return EMPTY;
              }),
            );
        }),
      ),
  );
}

function receiveRefreshResult(result: RefreshResult) {
  // Access token should always be set first to prevent spurious refreshes
  // This is guarded against in missingTokenRefresh$ va distinctUntilKeyChanged but
  // will document here as well
  setAccessToken(result.accessToken);
  setRefreshToken(result.refreshToken);
  setExpirationTime(result.expirationTime);
  setIdToken(result.idToken);
}

// We need to a way of continually polling some stuff to determine if we are approaching token expiration
export const refreshTrigger$ = timer(5 * 1000).pipe(repeat());

// The 'automatic refresh' logic that happens whenever
// we find ourselves within 30 seconds of expiration
const expiringTokenRefresh$ = combineLatest([accessToken$, refreshToken$, expirationTime$, refreshTrigger$])
  // We should not attempt to refresh if there is no refresh token
  // no expiration time, or the expiration logic is not within 5 mintues
  .pipe(
    filter(([_, refTok, expTime]) => {
      if (!refTok || !expTime) {
        return false;
      }
      const refreshAfter = expTime.getTime() - 30 * 1000;
      return refreshAfter < Date.now();
    }),
  )
  .pipe(
    switchMap(([accessToken, refreshToken]) =>
      currentlyRefreshing$
        .pipe(take(1))
        .pipe(concatMap((refreshing) => (!refreshing ? from([[accessToken, refreshToken]]) : EMPTY))),
    ),
  )
  .pipe(
    switchMap(([accessToken, refreshToken]) => {
      console.warn('Expiring token refresh triggered...performing automatic token refresh.');
      return performAutomaticRefresh(accessToken, refreshToken);
    }),
  );

expiringTokenRefresh$.subscribe({
  next: receiveRefreshResult,
  error: (e) => {
    console.error('expiring token refresh errored:', e);
  },
  complete: () => {
    console.warn('expiring token refresh completed');
  },
});

// The automatic refresh logic that happens whenever accessToken is null
// or we are past the expiration time
const missingTokenRefresh$: Observable<RefreshResult> = combineLatest([
  accessToken$,
  refreshToken$,
  currentlyRefreshing$,
])
  // If the refresh token should happen to change before the access token
  // we want to make sure we don't immediately fire another token refresh
  .pipe(distinctUntilChanged())
  // We have no accessToken but we do have a refresh token
  .pipe(filter(([accessToken, refreshToken]) => !accessToken && !!refreshToken && !isRefreshActive()))
  .pipe(
    switchMap(([accessToken, refreshToken]) =>
      currentlyRefreshing$
        // We would like only the current status of currentlyRefresh to decide
        .pipe(take(1))
        .pipe(
          switchMap((refreshing) => {
            // If someone is already refreshing we should do nothing
            if (refreshing) {
              return EMPTY;
            }
            console.warn('Missing token refresh triggered...performing automatic token refresh.');
            return performAutomaticRefresh(accessToken, refreshToken);
          }),
        ),
    ),
  );

missingTokenRefresh$.subscribe({
  next: receiveRefreshResult,
  error: (e) => {
    console.error('missing token refresh errored:', e);
  },
  complete: () => {
    console.warn('missing token refresh completed');
  },
});
